import type { AuthSession, Conversation } from "./types";
import { authenticatedFetch } from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

export type HistoryImportResult = {
  conversationId: string;
  receivedMessages: number;
  uniqueMessages: number;
  insertedMessages: number;
  duplicateMessages: number;
  coverageStart: string;
  coverageEnd: string;
  analysis?: { queued?: boolean; error?: string };
};

export type VsmartAction =
  | "health"
  | "list_context"
  | "list_tasks"
  | "list_alerts"
  | "propose_phenology_observation"
  | "propose_task_reschedule"
  | "propose_alert"
  | "propose_feedback"
  | "list_proposals"
  | "review_proposal"
  | "execute_proposal";

export type VsmartProposalStatus = "proposed" | "approved" | "rejected" | "executed" | "cancelled";

export type VsmartProposal = {
  id: string;
  external_request_id: string;
  source_conversation_id?: string | null;
  source_message_id?: string | null;
  tool_code: string;
  arguments: Record<string, unknown>;
  status: VsmartProposalStatus;
  requested_by?: Record<string, unknown>;
  confirmation_text?: string | null;
  result?: Record<string, unknown> | null;
  reviewed_by_external?: string | null;
  reviewed_by_email?: string | null;
  reviewed_by_role?: string | null;
  review_note?: string | null;
  reviewed_at?: string | null;
  executed_at?: string | null;
  execution_attempts?: number;
  last_execution_error?: string | null;
  last_execution_at?: string | null;
  created_at: string;
  updated_at: string;
  execution?: {
    supported: boolean;
    ready: boolean;
    blocker?: string | null;
    canReview: boolean;
    canExecute: boolean;
  };
};

export async function importRemoteConversationHistory(
  targetConversationId: string,
  importedConversation: Conversation,
  fileName: string,
  session: AuthSession,
): Promise<HistoryImportResult> {
  const messages = importedConversation.messages
    .filter((message) => Boolean(message.sentAtIso))
    .map((message) => ({
      id: message.id,
      sender: message.sender,
      body: message.body,
      sentAt: message.sentAt,
      sentAtIso: message.sentAtIso!,
      direction: message.direction,
      type: historyMessageType(message.type),
    }));
  if (!messages.length) throw new Error("HISTORY_IMPORT_WITHOUT_VALID_DATES");

  const fileSha256 = await sha256Hex(JSON.stringify(messages));
  const body = await request<{ item: HistoryImportResult }>(
    `/conversations/${encodeURIComponent(targetConversationId)}/history-import`,
    session,
    {
      method: "POST",
      body: JSON.stringify({
        fileName,
        fileSha256,
        triggerAnalysis: true,
        messages,
      }),
    },
  );
  return body.item;
}

export async function callRemoteVsmart(
  action: VsmartAction,
  args: Record<string, unknown>,
  session: AuthSession,
  options: { conversationId?: string; sourceMessageId?: string } = {},
) {
  return request<{ item: Record<string, unknown> }>(
    "/integrations/vsmart",
    session,
    {
      method: "POST",
      body: JSON.stringify({
        action,
        conversationId: options.conversationId,
        sourceMessageId: options.sourceMessageId,
        arguments: args,
      }),
    },
  ).then((response) => response.item);
}

async function request<T>(path: string, session: AuthSession, init: RequestInit = {}): Promise<T> {
  if (!API_BASE_URL) throw new Error("API_NOT_CONFIGURED");
  const response = await authenticatedFetch(`${API_BASE_URL}${path}`, session, init);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = typeof body?.error === "string" ? body.error : `HTTP_${response.status}`;
    throw new Error(code);
  }
  return body as T;
}

function historyMessageType(type: Conversation["messages"][number]["type"]) {
  if (["text", "audio", "image", "document", "system"].includes(type)) {
    return type as "text" | "audio" | "image" | "document" | "system";
  }
  return "text" as const;
}

async function sha256Hex(value: string) {
  const digest = new Uint8Array(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value)),
  );
  return [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
